import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FastTrackProviderSearchQuery } from '../../models/fast-track/fast-track-provider-search-query.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { EducationalProvider } from '../../models/educational-provider/educational-provider.interface';
import { DegreeObjective } from '../../models/application/degree-objective.interface';
import { Application } from '@edassist/api';
import {
	APPLICATION_STATUSES,
	ApplicationUtilityService,
	CLIENT_FEATURE_REFERENCE_CODES,
	ClientService
} from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class FastTrackService {
	constructor(
		private readonly http: HttpClient,
		private readonly clientService: ClientService
	) {}

	getFastTrackDegreeObjectives(
		participantId: number
	): Observable<Array<DegreeObjective>> {
		return this.http.get<Array<DegreeObjective>>(
			`participants/${participantId}/fast-track-degree-objectives`
		);
	}

	searchFastTrackProviders(
		participantId: number,
		fastTrackProviderSearchQuery: FastTrackProviderSearchQuery
	): Observable<Array<EducationalProvider>> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(
			fastTrackProviderSearchQuery
		);

		return this.http.get<Array<EducationalProvider>>(
			`participants/${participantId}/education-providers/fast-track-marketing-provider-search`,
			queryParams
		);
	}

	isFastTrackPaymentReady(applicationId: number): Observable<boolean> {
		return this.http.get<boolean>(
			`applications/${applicationId}/is-fast-track-payment-ready`
		);
	}

	isEligibleToSubmitFastTrackPayment(application: Application): boolean {
		return (
			this.isFastTrackExperience() &&
			this.clientService.isClientFeatureEnabled(
				CLIENT_FEATURE_REFERENCE_CODES.PAYMENT
			) &&
			application.applicationStatus.code === APPLICATION_STATUSES.approved &&
			application.programProvidersPaymentEnabled &&
			!application.fastTrackPaymentConfirmation
		);
	}

	isFastTrackPaymentProcessed(application: Application): boolean {
		return (
			this.isFastTrackExperience() &&
			this.clientService.isClientFeatureEnabled(
				CLIENT_FEATURE_REFERENCE_CODES.PAYMENT
			) &&
			application.applicationStatus.code === APPLICATION_STATUSES.approved &&
			application.programProvidersPaymentEnabled &&
			!!application.fastTrackPaymentConfirmation
		);
	}

	isEligibleToShowPaymentActionOrConfirmation(
		application: Application
	): boolean {
		return (
			ApplicationUtilityService.isReimbursement(application) &&
			this.isFastTrackExperience() &&
			this.clientService.isClientFeatureEnabled(
				CLIENT_FEATURE_REFERENCE_CODES.PAYMENT
			) &&
			application.applicationStatus.code === APPLICATION_STATUSES.approved &&
			application.programProvidersPaymentEnabled
		);
	}

	isFastTrackExperience(): boolean {
		return (
			this.clientService.isClientFeatureEnabled(
				CLIENT_FEATURE_REFERENCE_CODES.FAST_TRACK
			) ||
			this.clientService.isClientFeatureEnabled(
				CLIENT_FEATURE_REFERENCE_CODES.SITE_REDESIGN
			)
		);
	}
}
